import React, { useEffect, useMemo, useState, useRef, memo, Component } from 'react';
/**
 * SessionSummary 2.0 — Best-of-Both Flow (§19)
 *
 * Default: Quick Recap (~80%) — single screen, fast, "Volgende ronde" pre-focused
 * Escalation: Celebration Moments (~20%) — 1-3 moments before Quick Recap
 * Special: Reflection Card (~5%) — for <30% accuracy with personalized tip
 *
 * Refactored: 6 mei 2026
 */
import { motion, AnimatePresence } from 'framer-motion';
import { type ModuleId, type Question } from '../../content';
import type { FamilyMasteryState } from '../../mastery';
import { getFamilyMasteryKey } from '../../mastery';
import { useAppStore } from '../../app/hooks/useAppStore';
import { dataLayer, type PersonalRecords } from '../../data/dataLayer';
import { soundManager } from '../../utils/soundManager';
import { type SessionResult, CONFETTI } from './sessionSummaryData';
import { useSessionExperience } from '../hooks/useSessionExperience';
import { QuickRecap } from './sessionSummary/QuickRecap';
import { CelebrationMoments } from './sessionSummary/CelebrationMoments';
import { ReflectionCard } from './sessionSummary/ReflectionCard';
import { ReviewAnswersScreen } from './sessionSummary/ReviewAnswersScreen';
import { AbortedSummary } from './sessionSummary/AbortedSummary';
import { recordSessionScore } from './sessionSummary/recentSessionScores';
// F4.8 — Video Reward (APP_FEEL_2027_UPGRADE §11.1)
import { recordSessionAndCheckReward, markVideoRewardShown, type VideoRewardId } from '../../utils/videoRewardManager';
import { VideoRewardModal } from '../../components/shared/VideoRewardModal';
// Re-export for consumers
export type { SessionResult };
// ── Props ────────────────────────────────────────────────────
interface SessionSummaryProps {
  moduleId: ModuleId;
  sessionQuestions: Question[];
  sessionAnswers: SessionResult[];
  preFamilyMastery: Record<string, FamilyMasteryState>;
  isFluency?: boolean;
  labMode?: string;
  onExit: () => void;
  onPracticeAgain?: () => void;
  xpGained: number;
}
// ── Component ────────────────────────────────────────────────
export function SessionSummary({
  moduleId,
  sessionQuestions,
  sessionAnswers,
  preFamilyMastery,
  isFluency = false,
  labMode,
  onExit,
  onPracticeAgain,
  xpGained
}: SessionSummaryProps) {
  const {
    state,
    selectedGroup
  } = useAppStore();
  const currentFamilyMastery = state.familyMastery || {};
  const correctCount = sessionAnswers.filter(a => a.isCorrect).length;
  const totalQuestions = sessionAnswers.length;
  const percentage = totalQuestions > 0 ? Math.round(correctCount / totalQuestions * 100) : 0;
  const isGreat = percentage >= 80;
  // Calculate total time
  const totalTimeMs = useMemo(() => {
    return sessionAnswers.reduce((sum, a) => sum + (a.timeMs || 0), 0);
  }, [sessionAnswers]);
  // Practiced families — memoized so the array reference is stable
  // (prevents downstream effects from re-firing on every render).
  const practicedFamilyIds = useMemo(() => {
    return Array.from(new Set(sessionAnswers.map(q => q.familyId).filter((id): id is string => !!id))).slice(0, 4);
  }, [sessionAnswers]);
  // Check for records broken — guarded so it only runs once per mount.
  const [recordsBroken, setRecordsBroken] = useState(false);
  const recordsCheckedRef = useRef(false);
  useEffect(() => {
    if (!labMode || recordsCheckedRef.current) return;
    recordsCheckedRef.current = true;
    dataLayer.getPersonalRecords().then(records => {
      let broken = false;
      const newRecords: PersonalRecords = {
        ...records
      };
      if (labMode === 'fluency') {
        const currentFluency = newRecords.fluency || {
          fastestAvgMs: Infinity,
          longestStreak: 0,
          bestScore: 0
        };
        const avgMs = totalTimeMs / totalQuestions;
        if (avgMs > 0 && avgMs < currentFluency.fastestAvgMs) {
          broken = true;
          newRecords.fluency = {
            ...currentFluency,
            fastestAvgMs: avgMs
          };
        }
        if (percentage > currentFluency.bestScore) {
          broken = true;
          newRecords.fluency = {
            ...currentFluency,
            bestScore: percentage
          };
        }
      } else if (labMode === 'sprint') {
        const familyId = practicedFamilyIds[0];
        if (familyId) {
          const currentSprint = newRecords.sprint?.[familyId] || {
            bestScore: 0,
            fastestTimeMs: Infinity
          };
          if (percentage > currentSprint.bestScore) {
            broken = true;
            newRecords.sprint = {
              ...newRecords.sprint,
              [familyId]: {
                ...currentSprint,
                bestScore: percentage
              }
            };
          }
          if (totalTimeMs < currentSprint.fastestTimeMs) {
            broken = true;
            newRecords.sprint = {
              ...newRecords.sprint,
              [familyId]: {
                ...currentSprint,
                fastestTimeMs: totalTimeMs
              }
            };
          }
        }
      }
      if (broken) {
        setRecordsBroken(true);
        dataLayer.savePersonalRecords(newRecords);
      }
    });
  }, [labMode, totalTimeMs, totalQuestions, percentage, practicedFamilyIds]);
  // Save lab session
  useEffect(() => {
    if (labMode) {
      dataLayer.saveLastOefenlabSession({
        modeId: labMode,
        familyId: practicedFamilyIds[0],
        timestamp: Date.now(),
        score: percentage
      });
      // Check for newly unlocked challenges
      for (const familyId of practicedFamilyIds) {
        const masteryKey = getFamilyMasteryKey(familyId, selectedGroup);
        const pre = preFamilyMastery[masteryKey]?.masteryScore || 0;
        const post = currentFamilyMastery[masteryKey]?.masteryScore || 0;
        if (pre < 70 && post >= 70) {
          dataLayer.setNewlyUnlockedChallenge(familyId);
          break;
        }
      }
    }
  }, [labMode, practicedFamilyIds, percentage, preFamilyMastery, currentFamilyMastery, selectedGroup]);
  // Track recent session scores for decay-detection (§20.7 G6).
  // Only record real practice sessions, not lab modes.
  useEffect(() => {
    if (!labMode && totalQuestions > 0) {
      recordSessionScore(percentage);
    }
    // Mount-only: record this session's score exactly once. Adding
    // labMode/totalQuestions/percentage would double-record if upstream
    // state churns while the summary is open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // F4.8 — Video Reward: tally cumulative correct, trigger one-shot
  // celebration when crossing 100 correct answers. Runs once per mount.
  const [activeReward, setActiveReward] = useState<VideoRewardId | null>(null);
  const rewardCheckedRef = useRef(false);
  useEffect(() => {
    if (rewardCheckedRef.current) return;
    rewardCheckedRef.current = true;
    // Only real practice and lab sessions count — skip empty/aborted sessions.
    if (correctCount <= 0) return;
    const trigger = recordSessionAndCheckReward(correctCount);
    if (trigger) setActiveReward(trigger);
    // Mount-only: F4.8 video reward must trigger at most once per summary
    // mount. rewardCheckedRef guards re-entry; deps would re-fire on
    // correctCount churn and re-trigger the reward.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleRewardClose = () => {
    if (activeReward) markVideoRewardShown(activeReward);
    setActiveReward(null);
  };
  // Determine experience mode and triggers
  const experience = useSessionExperience(sessionAnswers, preFamilyMastery, currentFamilyMastery, labMode, state, selectedGroup, recordsBroken);
  // Confetti for great sessions
  useEffect(() => {
    if (isGreat || experience.triggers.foutloos || recordsBroken) {
      soundManager.playGoalReached();
    }
  }, [isGreat, experience.triggers.foutloos, recordsBroken]);
  // State for celebration flow
  const [showCelebration, setShowCelebration] = useState(experience.mode === 'celebrated');
  const [celebrationComplete, setCelebrationComplete] = useState(false);
  useEffect(() => {
    if (experience.mode === 'celebrated') {
      setShowCelebration(true);
    }
  }, [experience.mode]);
  const handleCelebrationComplete = () => {
    setCelebrationComplete(true);
    setShowCelebration(false);
  };
  const handleContinue = () => {
    if (onPracticeAgain) {
      onPracticeAgain();
    }
  };
  // Review-screen state (§20.3 G2)
  const [reviewState, setReviewState] = useState<{
    open: boolean;
    index?: number;
  }>({
    open: false
  });
  const handleReviewErrors = () => {
    setReviewState({
      open: true
    });
  };
  const handleReviewQuestion = (index: number) => {
    setReviewState({
      open: true,
      index
    });
  };
  const handleReviewClose = () => {
    setReviewState({
      open: false
    });
  };
  // G6 edge case: session ended with zero answers (abort)
  if (totalQuestions === 0) {
    return <AbortedSummary onPracticeAgain={onPracticeAgain} onExit={onExit} data-id="element-11371" />;
  }
  // Render flow based on mode
  if (experience.mode === 'reflection') {
    return <>
        <ReflectionCard djayTip={experience.djayTip} topErrorFamily={practicedFamilyIds[0]} onReviewErrors={handleReviewErrors} onLabSprint={onPracticeAgain} onExit={onExit} data-id="element-11372" />
        <AnimatePresence data-id="element-11373">
          {reviewState.open && <ReviewAnswersScreen key="review" sessionAnswers={sessionAnswers} sessionQuestions={sessionQuestions} initialIndex={reviewState.index} onBack={handleReviewClose} data-id="element-11374" />}
        </AnimatePresence>
      </>;
  }
  return <>
      {/* Confetti Background */}
      {(isGreat || experience.triggers.foutloos || recordsBroken) && <div className="pointer-events-none fixed inset-0 z-40" data-id="element-11375">
          {CONFETTI.map((dot, i) => <motion.div key={i} initial={{
        scale: 0,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} transition={{
        delay: dot.delay,
        duration: 0.4
      }} className={`absolute ${dot.color} ${dot.size} rounded-full`} style={{
        top: dot.top,
        left: dot.left,
        right: dot.right
      }} data-id="element-11376" />)}
        </div>}

      <AnimatePresence mode="wait" data-id="element-11377">
        {showCelebration && !celebrationComplete && <CelebrationMoments key="celebration" triggers={experience.triggers} onComplete={handleCelebrationComplete} data-id="element-11378" />}

        {(!showCelebration || celebrationComplete) && <QuickRecap key="recap" sessionAnswers={sessionAnswers} xpGained={xpGained} streak={state.streak?.currentTotalStreak || 0} djayTip={experience.djayTip} djayEmotion={experience.djayEmotion} preFamilyMastery={preFamilyMastery} currentFamilyMastery={currentFamilyMastery} onNextRound={handleContinue} onExit={onExit} onReviewErrors={handleReviewErrors} onReviewQuestion={handleReviewQuestion} data-id="element-11379" />}
      </AnimatePresence>

      <AnimatePresence data-id="element-11380">
        {reviewState.open && <ReviewAnswersScreen key="review" sessionAnswers={sessionAnswers} sessionQuestions={sessionQuestions} initialIndex={reviewState.index} onBack={handleReviewClose} data-id="element-11381" />}
      </AnimatePresence>

      {/* F4.8 — One-shot reward modal, layered above the summary */}
      {activeReward && <VideoRewardModal rewardId={activeReward} isOpen={true} onClose={handleRewardClose} data-id="element-11382" />}
    </>;
}